



















































































































































import { Component, Prop, Vue } from 'vue-property-decorator'
import StatisticalItem from './StatisticalItem.vue'
import { RegenerateFinanceWealthWayDailyStat } from '@/api'

interface BalanceRow {
  dayNetTotal?: number
  endBalanceCalc?: number
  balanceSnapshot?: number
  reconcileDiff?: number
}

interface BalancePair {
  today: BalanceRow
  yesterday: BalanceRow
}

interface CategoryRow {
  categoryName?: string
  inQty?: number
  outQty?: number
  netQty?: number
  txCount?: number
}

interface PvRow {
  normalOrderPv?: number
  distOrderPv?: number
  queueOrderPv?: number
  repurchaseOrderPv?: number
  specialOrderPv?: number
  bizOrderPvTotal?: number
  platformGainPvSnapshot?: number
  queueOrderCount?: number
}

interface PvPair {
  today: PvRow
  yesterday: PvRow
}

interface PanelData {
  mungBeanBalanceMap?: BalancePair
  pointsBalanceMap?: BalancePair
  mungBeanCategoryMap?: Record<string, CategoryRow[]>
  pointsCategoryMap?: Record<string, CategoryRow[]>
  bizPvReconcileMap?: PvPair
}

function emptyPvPair(): PvPair {
  return { today: {}, yesterday: {} }
}

function formatYmd(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function normalizeYmd(raw: string): string {
  const parts = raw.split('-')
  if (parts.length !== 3) return raw
  return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`
}

function todayStr(): string {
  return formatYmd(new Date())
}

function marchFirstStr(): string {
  return `${new Date().getFullYear()}-03-01`
}

function aug2025FirstStr(): string {
  return '2025-08-01'
}

function currentYm(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function aug2025Ym(): string {
  return '2025-08'
}

/** yyyy-MM → 当月首日 */
function monthFirstDay(ym: string): string {
  return `${ym}-01`
}

/** yyyy-MM → 当月末日（不超过今天） */
function monthLastDay(ym: string): string {
  const parts = ym.split('-')
  const y = Number(parts[0])
  const m = Number(parts[1])
  const last = new Date(y, m, 0)
  const today = new Date()
  const end = last.getTime() > today.getTime() ? today : last
  return formatYmd(end)
}

/** 枚举月份区间，每月一项 */
function listMonths(startYm: string, endYm: string): Array<{ ym: string; startDate: string; endDate: string; days: number }> {
  const result: Array<{ ym: string; startDate: string; endDate: string; days: number }> = []
  const [sy, sm] = startYm.split('-').map(Number)
  const [ey, em] = endYm.split('-').map(Number)
  let y = sy
  let m = sm
  while (y < ey || (y === ey && m <= em)) {
    const ym = `${y}-${String(m).padStart(2, '0')}`
    const startDate = monthFirstDay(ym)
    const endDate = monthLastDay(ym)
    result.push({ ym, startDate, endDate, days: countDays(startDate, endDate) })
    m += 1
    if (m > 12) {
      m = 1
      y += 1
    }
  }
  return result
}

function emptyBalancePair(): BalancePair {
  return { today: {}, yesterday: {} }
}

interface DateChunk {
  startDate: string
  endDate: string
  days: number
}

function countDays(startDate: string, endDate: string): number {
  const dayMs = 86400000
  return Math.floor((new Date(endDate).getTime() - new Date(startDate).getTime()) / dayMs) + 1
}

function splitDateRange(startDate: string, endDate: string, chunkDays: number): DateChunk[] {
  const chunks: DateChunk[] = []
  let cur = new Date(startDate)
  const end = new Date(endDate)
  while (cur.getTime() <= end.getTime()) {
    const chunkStart = formatYmd(cur)
    const chunkEnd = new Date(cur)
    chunkEnd.setDate(chunkEnd.getDate() + chunkDays - 1)
    if (chunkEnd.getTime() > end.getTime()) {
      chunkEnd.setTime(end.getTime())
    }
    const chunkEndStr = formatYmd(chunkEnd)
    chunks.push({
      startDate: chunkStart,
      endDate: chunkEndStr,
      days: countDays(chunkStart, chunkEndStr),
    })
    cur = new Date(chunkEnd)
    cur.setDate(cur.getDate() + 1)
  }
  return chunks
}

@Component({ components: { StatisticalItem } })
export default class FinanceWealthWayDailyPanel extends Vue {
  @Prop({ required: true }) data!: PanelData

  private regenerating = false
  private regenerateProgress = 0
  private regenerateStatus: '' | 'success' | 'error' = ''
  private regenerateStatusText = ''
  private regenerateRange: string[] = [aug2025FirstStr(), todayStr()]
  private regenerateMonthRange: string[] = [aug2025Ym(), currentYm()]
  private pickerOptions = {
    disabledDate(time: Date): boolean {
      return time.getTime() > Date.now()
    },
  }
  private monthPickerOptions = {
    disabledDate(time: Date): boolean {
      return time.getTime() > Date.now()
    },
  }

  private get mb(): BalancePair {
    return this.data.mungBeanBalanceMap || emptyBalancePair()
  }

  private get pt(): BalancePair {
    return this.data.pointsBalanceMap || emptyBalancePair()
  }

  private get pv(): PvPair {
    return this.data.bizPvReconcileMap || emptyPvPair()
  }

  private get mungBeanCats(): CategoryRow[] {
    const list = (this.data.mungBeanCategoryMap && this.data.mungBeanCategoryMap.yesterday) || []
    return list.slice(0, 10)
  }

  private get pointsCats(): CategoryRow[] {
    const list = (this.data.pointsCategoryMap && this.data.pointsCategoryMap.yesterday) || []
    return list.slice(0, 10)
  }

  private get defaultRangeHint(): string {
    return `${aug2025FirstStr()} 至 ${todayStr()}`
  }

  private formatDiff(v: number | undefined): string {
    if (v === undefined || v === null) return '-'
    return String(v)
  }

  private diffClass(v: number | undefined): string {
    if (v === undefined || v === null) return 'diff-ok'
    const n = Math.abs(Number(v))
    if (n <= 0.01) return 'diff-ok'
    if (n <= 1) return 'diff-warn'
    return 'diff-bad'
  }

  public handleRegenerate(): void {
    if (this.regenerating) {
      return
    }
    if (!this.regenerateRange || this.regenerateRange.length !== 2) {
      this.$message.warning('请选择日期范围')
      return
    }
    const [startRaw, endRaw] = this.regenerateRange
    const startDate = normalizeYmd(startRaw)
    const endDate = normalizeYmd(endRaw)
    const totalDays = countDays(startDate, endDate)
    this.$confirm(
      `将补跑 ${startDate} 至 ${endDate} 共 ${totalDays} 天的账户流水对账 + 业务PV日汇总（按日覆盖更新），将分段执行并显示进度，请勿关闭页面，是否继续？`,
      '批量生成历史数据',
      { type: 'warning', confirmButtonText: '开始生成', cancelButtonText: '取消' }
    ).then(() => {
      this.runRegenerateByDateRange(startDate, endDate, totalDays)
    }).catch(() => undefined)
  }

  public handleRegenerateByMonth(): void {
    if (this.regenerating) {
      return
    }
    if (!this.regenerateMonthRange || this.regenerateMonthRange.length !== 2) {
      this.$message.warning('请选择月份范围')
      return
    }
    const [startYm, endYm] = this.regenerateMonthRange
    if (startYm > endYm) {
      this.$message.warning('开始月份不能晚于结束月份')
      return
    }
    const months = listMonths(startYm, endYm)
    const totalDays = months.reduce((s, m) => s + m.days, 0)
    this.$confirm(
      `将按月补跑 ${startYm} 至 ${endYm} 共 ${months.length} 个月、约 ${totalDays} 天（账户流水 + 业务PV），未上线日显示为 0。耗时较长，请勿关闭页面，是否继续？`,
      '按月批量补跑',
      { type: 'warning', confirmButtonText: '开始生成', cancelButtonText: '取消' }
    ).then(() => {
      this.runRegenerateByMonth(months, totalDays)
    }).catch(() => undefined)
  }

  private clearRegenerateStatus(): void {
    this.regenerateStatus = ''
    this.regenerateStatusText = ''
    this.regenerateProgress = 0
  }

  private async runRegenerateByDateRange(startDate: string, endDate: string, totalDays: number): Promise<void> {
    this.regenerating = true
    this.regenerateStatus = ''
    this.regenerateProgress = 0
    this.regenerateStatusText = `准备补跑 ${startDate} 至 ${endDate}，共 ${totalDays} 天…`
    let doneDays = 0
    try {
      doneDays = await this.runRegenerateChunks(startDate, endDate, totalDays)
      this.regenerateStatus = 'success'
      this.regenerateStatusText = `补跑完成：${startDate} 至 ${endDate}，共 ${totalDays} 天`
      this.regenerateProgress = 100
      this.$message.success(this.regenerateStatusText)
      this.$emit('refresh')
    } catch (e) {
      const msg = (e && (e as Error).message) ? (e as Error).message : '补跑失败，请稍后重试'
      this.regenerateStatus = 'error'
      this.regenerateStatusText = `补跑失败（已完成 ${doneDays}/${totalDays} 天）：${msg}`
      this.$message.error(this.regenerateStatusText)
    } finally {
      this.regenerating = false
    }
  }

  private async runRegenerateChunks(
    startDate: string,
    endDate: string,
    totalDays: number,
    progressCtx?: { doneDaysBefore: number; totalDaysAll: number; monthLabel?: string }
  ): Promise<number> {
    const chunks = splitDateRange(startDate, endDate, 5)
    let doneDays = 0
    const doneBefore = progressCtx ? progressCtx.doneDaysBefore : 0
    const totalAll = progressCtx ? progressCtx.totalDaysAll : totalDays
    const monthPrefix = progressCtx && progressCtx.monthLabel ? `${progressCtx.monthLabel} ` : ''

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i]
      const allDone = doneBefore + doneDays
      this.regenerateStatusText = `${monthPrefix}正在补跑 ${chunk.startDate} 至 ${chunk.endDate}（${i + 1}/${chunks.length} 段，总进度 ${allDone}/${totalAll} 天）…`
      await RegenerateFinanceWealthWayDailyStat({
        startDate: chunk.startDate,
        endDate: chunk.endDate,
      })
      doneDays += chunk.days
      this.regenerateProgress = Math.min(100, Math.round(((doneBefore + doneDays) / totalAll) * 100))
    }
    return doneDays
  }

  private async runRegenerateByMonth(
    months: Array<{ ym: string; startDate: string; endDate: string; days: number }>,
    totalDays: number
  ): Promise<void> {
    this.regenerating = true
    this.regenerateStatus = ''
    this.regenerateProgress = 0
    let doneDays = 0
    const startYm = months[0].ym
    const endYm = months[months.length - 1].ym
    this.regenerateStatusText = `准备按月补跑 ${startYm} 至 ${endYm}，共 ${months.length} 个月…`

    try {
      for (let mi = 0; mi < months.length; mi++) {
        const month = months[mi]
        const label = `${month.ym}（${mi + 1}/${months.length} 月）`
        const monthDone = await this.runRegenerateChunks(month.startDate, month.endDate, month.days, {
          doneDaysBefore: doneDays,
          totalDaysAll: totalDays,
          monthLabel: label,
        })
        doneDays += monthDone
      }
      this.regenerateStatus = 'success'
      this.regenerateStatusText = `按月补跑完成：${startYm} 至 ${endYm}，共 ${months.length} 个月、${totalDays} 天`
      this.regenerateProgress = 100
      this.$message.success(this.regenerateStatusText)
      this.$emit('refresh')
    } catch (e) {
      const msg = (e && (e as Error).message) ? (e as Error).message : '补跑失败，请稍后重试'
      this.regenerateStatus = 'error'
      this.regenerateStatusText = `按月补跑失败（已完成 ${doneDays}/${totalDays} 天）：${msg}`
      this.$message.error(this.regenerateStatusText)
    } finally {
      this.regenerating = false
    }
  }
}
